/**
 * Edit Event - Club admin interface for editing existing events
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/api';
import { AdminPageLayout, FormInput, FormTextArea, FormSelect, FormCheckbox, FormMultiSelect, FormColorPicker, FormDateTimePicker, Button, ConfirmDialog, LoadingState } from '../../components';
import { ErrorBanner } from '../../components/common';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useAuth } from '../../contexts/AuthContext';
import { toUTCString, toLocalInputString } from '../../utils/datetime';

// Training event types that support session templates - moved outside component to avoid re-creating on every render
const TRAINING_EVENT_TYPES = ['training_session', 'skill_session', 'fitness_session', 'practice'];

const EditEvent = () => {
  const navigate = useNavigate();
  const { calendarId, eventId } = useParams();
  const { user } = useAuth();
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(true);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [sessionMode, setSessionMode] = useState('template'); // 'template' or 'adhoc'
  const [sessionTemplates, setSessionTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activityTemplates, setActivityTemplates] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [sessionActivities, setSessionActivities] = useState([]);
  const [existingSessionId, setExistingSessionId] = useState(null);

  const eventTypes = [
    { value: 'practice', label: 'Practice' },
    { value: 'training_session', label: 'Training Session' },
    { value: 'skill_session', label: 'Skill Session' },
    { value: 'fitness_session', label: 'Fitness Session' },
    { value: 'game', label: 'Game' },
    { value: 'tournament', label: 'Tournament' },
    { value: 'scrimmage', label: 'Scrimmage' },
    { value: 'friendly_match', label: 'Friendly Match' },
    { value: 'team_meeting', label: 'Team Meeting' },
    { value: 'parent_meeting', label: 'Parent Meeting' },
    { value: 'coach_meeting', label: 'Coach Meeting' },
    { value: 'club_meeting', label: 'Club Meeting' },
    // { value: 'tryout', label: 'Tryout' }, // DISABLED
    { value: 'camp', label: 'Camp' },
    { value: 'clinic', label: 'Clinic' },
    { value: 'fundraiser', label: 'Fundraiser' },
    { value: 'social_event', label: 'Social Event' },
    { value: 'custom', label: 'Custom Event' }
  ];

  const {
    formData,
    errors,
    touched,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFormData,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      selected_calendar_id: calendarId || '',
      title: '',
      description: '',
      event_type: 'practice',
      visibility_roles: ['all'],
      start_time: '',
      end_time: '',
      location: '',
      is_mandatory: false,
      max_participants: '',
      color_code: '#1565C0',
      session_mode: 'template',
      session_template_id: '',
      activities: []
    },
    validationSchema: (values) => {
      const newErrors = {};

      if (!values.title.trim()) {
        newErrors.title = 'Event title is required';
      }

      if (!values.start_time) {
        newErrors.start_time = 'Start time is required';
      }

      if (!values.end_time) {
        newErrors.end_time = 'End time is required';
      }

      if (values.start_time && values.end_time && values.start_time >= values.end_time) {
        newErrors.end_time = 'End time must be after start time';
      }

      if (!values.visibility_roles || values.visibility_roles.length === 0) {
        newErrors.visibility_roles = 'At least one visibility role is required';
      }

      // Validate session data for training events
      if (TRAINING_EVENT_TYPES.includes(values.event_type)) {
        if (values.session_mode === 'template' && !values.session_template_id) {
          newErrors.session_template_id = 'Session template is required';
        } else if (values.session_mode === 'adhoc' && (!values.activities || values.activities.length === 0)) {
          newErrors.activities = 'At least one activity is required for ad-hoc sessions';
        }
      }

      return newErrors;
    },
    onSubmit: async (values) => {
      const changedData = {};
      Object.keys(values).forEach(key => {
        if (key !== 'selected_calendar_id' && 
            key !== 'session_mode' && 
            key !== 'session_template_id' && 
            key !== 'activities' &&
            JSON.stringify(values[key]) !== JSON.stringify(originalData[key])) {
          changedData[key] = values[key];
        }
      });

      // Check if calendar changed
      const calendarChanged = values.selected_calendar_id !== originalData.selected_calendar_id;
      if (calendarChanged) {
        changedData.calendar_id = values.selected_calendar_id;
      }

      try {
        // Update event if there are changes
        if (Object.keys(changedData).length > 0) {
          const eventData = {
            ...changedData,
            start_time: changedData.start_time ? toUTCString(changedData.start_time) : undefined,
            end_time: changedData.end_time ? toUTCString(changedData.end_time) : undefined,
            max_participants: changedData.max_participants ? parseInt(changedData.max_participants) : null
          };

          // Remove undefined values
          Object.keys(eventData).forEach(key => eventData[key] === undefined && delete eventData[key]);

          await apiService.updateCalendarEvent(eventId, eventData);
        }

        // Handle training session for training event types
        if (TRAINING_EVENT_TYPES.includes(values.event_type) && (values.session_template_id || values.activities?.length > 0)) {
          // Get branch_id - training sessions MUST be at branch level
          const calendar = calendars.find(cal => cal.id === values.selected_calendar_id);
          let branchId = null;

          // Try to get branch_id from various sources
          if (calendar?.scope === 'branch') {
            branchId = calendar.scope_id;
          } else if (user?.branch_id) {
            branchId = user.branch_id;
          } else if (user?.location_id) {
            branchId = user.location_id;
          }

          if (!branchId) {
            throw new Error(
              'Training sessions require a branch. Please select a branch-level calendar or contact your administrator.'
            );
          }

          const trainingSessionData = {
            branch_id: branchId,
            scheduled_date: toUTCString(values.start_time),
            event_id: eventId,
          };

          // Add template or activities based on mode
          if (values.session_mode === 'template') {
            trainingSessionData.session_template_id = values.session_template_id;
            trainingSessionData.activities = values.activities.map(activity => ({
              activity_template_id: activity.activity_template_id,
              planned_duration_mins: parseInt(activity.planned_duration_mins),
              sequence_order: activity.sequence_order
            }));
          } else {
            // Ad-hoc session - no template reference
            trainingSessionData.activities = values.activities.map(activity => ({
              activity_template_id: activity.activity_template_id,
              planned_duration_mins: parseInt(activity.planned_duration_mins),
              sequence_order: activity.sequence_order
            }));
          }

          // Update or create training session
          if (existingSessionId) {
            // Update existing session
            await apiService.updateTrainingSession(existingSessionId, trainingSessionData);
          } else {
            // Create new session
            await apiService.createTrainingSession(branchId, trainingSessionData);
          }
        }

        navigate(`/admin/calendars/${values.selected_calendar_id}/events`);
      } catch (err) {
        setError(err.message || 'Failed to update event');
        throw err;
      }
    }
  });

  // Fetch available calendars on mount
  useEffect(() => {
    const loadCalendars = async () => {
      try {
        setLoadingCalendars(true);
        const response = await apiService.getCalendars({ active_only: true });
        setCalendars(response.calendars || []);
        setLoadingCalendars(false);
      } catch (err) {
        setError(err.message || 'Failed to load calendars');
        setLoadingCalendars(false);
      }
    };

    loadCalendars();
  }, []);

  // Fetch available roles on mount
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true);
        const response = await apiService.getClubRoles(true);
        setRoles(response.roles || []);
        setLoadingRoles(false);
      } catch (err) {
        setError(err.message || 'Failed to load roles');
        setLoadingRoles(false);
      }
    };

    loadRoles();
  }, []);

  useEffect(() => {
    loadEvent();
  }, [eventId, calendarId]);

  // Load session templates when event type changes to training
  useEffect(() => {
    const loadSessionTemplates = async () => {
      if (!TRAINING_EVENT_TYPES.includes(formData.event_type)) {
        setSessionTemplates([]);
        setSelectedTemplate(null);
        return;
      }

      if (!user) {
        return;
      }

      try {
        setLoadingTemplates(true);
        let response;

        // Try to get branch_id from current user or calendar
        const calendar = formData.selected_calendar_id
          ? calendars.find(cal => cal.id === formData.selected_calendar_id)
          : null;

        // Determine which endpoint to use based on calendar scope or user role
        // Priority: calendar scope > user's branch > user's club
        let branchId = null;
        let clubId = null;

        if (calendar?.scope === 'branch') {
          // Calendar is branch-scoped, use branch endpoint
          branchId = calendar.scope_id;
        } else if (user.branch_id || user.location_id) {
          // User is branch-level, use branch endpoint
          branchId = user.branch_id || user.location_id;
        } else {
          // Default to club endpoint
          clubId = user.club_id || calendar?.club_id;
        }

        if (branchId) {
          response = await apiService.getBranchSessionTemplates(branchId, {
            active_only: true,
            include_activities: true
          });
        } else if (clubId) {
          response = await apiService.getClubSessionTemplates(clubId, {
            active_only: true,
            include_activities: true
          });
        }

        // Filter out platform templates
        const filteredTemplates = (response?.templates || []).filter(
          template => template.scope !== 'platform'
        );

        setSessionTemplates(filteredTemplates);
        setLoadingTemplates(false);
      } catch (err) {
        console.error('Failed to load session templates:', err);
        setLoadingTemplates(false);
      }
    };

    loadSessionTemplates();
  }, [formData.event_type, formData.selected_calendar_id, calendars, user]);

  // Load activity templates when in ad-hoc mode
  useEffect(() => {
    const loadActivityTemplates = async () => {
      if (!TRAINING_EVENT_TYPES.includes(formData.event_type) || sessionMode !== 'adhoc') {
        setActivityTemplates([]);
        return;
      }

      if (!user) {
        return;
      }

      try {
        setLoadingActivities(true);
        let response;

        const calendar = formData.selected_calendar_id
          ? calendars.find(cal => cal.id === formData.selected_calendar_id)
          : null;

        // Determine which endpoint to use based on calendar scope or user role
        // Priority: calendar scope > user's branch > user's club
        let branchId = null;
        let clubId = null;

        if (calendar?.scope === 'branch') {
          // Calendar is branch-scoped, use branch endpoint
          branchId = calendar.scope_id;
        } else if (user.branch_id || user.location_id) {
          // User is branch-level, use branch endpoint
          branchId = user.branch_id || user.location_id;
        } else {
          // Default to club endpoint
          clubId = user.club_id || calendar?.club_id;
        }

        if (branchId) {
          response = await apiService.getBranchActivityTemplates(branchId, {
            active_only: true
          });
        } else if (clubId) {
          response = await apiService.getClubActivityTemplates(clubId, {
            active_only: true
          });
        }

        // Filter out platform templates
        const filteredActivities = (response?.templates || []).filter(
          template => template.scope !== 'platform'
        );

        setActivityTemplates(filteredActivities);
        setLoadingActivities(false);
      } catch (err) {
        console.error('Failed to load activity templates:', err);
        setLoadingActivities(false);
      }
    };

    loadActivityTemplates();
  }, [formData.event_type, sessionMode, formData.selected_calendar_id, calendars, user]);

  // Load existing training session data if it exists
  useEffect(() => {
    const loadExistingSession = async () => {
      if (!eventId || !TRAINING_EVENT_TYPES.includes(formData.event_type)) {
        return;
      }

      try {
        // Get training session linked to this event
        const response = await apiService.getEventTrainingSession(eventId);
        if (response && response.training_session) {
          const session = response.training_session;
          setExistingSessionId(session.id);
          
          // Set mode based on whether session has a template
          const mode = session.session_template_id ? 'template' : 'adhoc';
          setSessionMode(mode);
          setFieldValue('session_mode', mode);
          
          if (session.session_template_id) {
            setFieldValue('session_template_id', session.session_template_id);
            setSelectedTemplate(session.session_template);
          }
          
          if (session.activities && session.activities.length > 0) {
            setSessionActivities(session.activities);
            setFieldValue('activities', session.activities);
          }
        }
      } catch (err) {
        // No session exists yet, that's ok
        console.log('No existing training session found for this event');
      }
    };

    if (formData.event_type && calendars.length > 0) {
      loadExistingSession();
    }
  }, [eventId, formData.event_type, calendars]);

  // Handle session mode change
  const handleSessionModeChange = (mode) => {
    setSessionMode(mode);
    setFieldValue('session_mode', mode);
    
    // Clear current selections
    setSelectedTemplate(null);
    setSessionActivities([]);
    setFieldValue('session_template_id', '');
    setFieldValue('activities', []);
  };

  // Handle session template selection
  const handleTemplateSelect = async (templateId) => {
    setFieldValue('session_template_id', templateId);

    if (!templateId) {
      setSelectedTemplate(null);
      setSessionActivities([]);
      setFieldValue('activities', []);
      return;
    }

    try {
      const template = sessionTemplates.find(t => t.id === templateId);
      if (template && template.activities) {
        setSelectedTemplate(template);
        const activities = template.activities.map((activity, index) => ({
          ...activity,
          planned_duration_mins: activity.duration_mins,
          sequence_order: activity.sequence_order || index
        }));
        setSessionActivities(activities);
        setFieldValue('activities', activities);
      }
    } catch (err) {
      console.error('Failed to load template details:', err);
    }
  };

  // Handle activity duration change
  const handleActivityDurationChange = (index, newDuration) => {
    const updatedActivities = [...sessionActivities];
    updatedActivities[index] = {
      ...updatedActivities[index],
      planned_duration_mins: parseInt(newDuration) || 0
    };
    setSessionActivities(updatedActivities);
    setFieldValue('activities', updatedActivities);
  };

  // Handle adding activity (ad-hoc mode)
  const handleAddActivity = (activityTemplateId) => {
    const activityTemplate = activityTemplates.find(a => a.id === activityTemplateId);
    if (!activityTemplate) return;

    const newActivity = {
      activity_template_id: activityTemplate.id,
      activity_name: activityTemplate.name,
      activity_type: activityTemplate.activity_type,
      planned_duration_mins: activityTemplate.default_duration_mins || 15,
      sequence_order: sessionActivities.length
    };

    const updatedActivities = [...sessionActivities, newActivity];
    setSessionActivities(updatedActivities);
    setFieldValue('activities', updatedActivities);
  };

  // Handle removing activity (ad-hoc mode)
  const handleRemoveActivity = (index) => {
    const updatedActivities = sessionActivities.filter((_, i) => i !== index);
    // Update sequence orders
    updatedActivities.forEach((activity, i) => {
      activity.sequence_order = i;
    });
    setSessionActivities(updatedActivities);
    setFieldValue('activities', updatedActivities);
  };

  // Build visibility options (special values + role IDs)
  const visibilityOptions = [
    {
      value: 'all',
      label: 'All Members',
      description: 'Everyone in this calendar (parents, players, staff, etc.)'
    },
    {
      value: 'staff',
      label: 'Staff Only',
      description: 'All staff members (admins, coaches, directors, managers, etc.)'
    },
    ...roles.map(role => ({
      value: role.id,
      label: role.name,
      description: role.is_staff ? '👤 Staff Role' : '👥 Member Role'
    }))
  ];

  const loadEvent = async () => {
    try {
      setLoading(true);
      // Use getCalendarEvent to fetch single event directly instead of filtering all events
      const event = await apiService.getCalendarEvent(eventId);

      if (!event) {
        setError('Event not found');
        setLoading(false);
        return;
      }

      const eventData = {
        selected_calendar_id: event.calendar_id || calendarId,
        title: event.title || '',
        description: event.description || '',
        event_type: event.event_type || 'practice',
        visibility_roles: event.visibility_roles || ['all'],
        start_time: toLocalInputString(event.start_time) || '',
        end_time: toLocalInputString(event.end_time) || '',
        location: event.location || '',
        is_mandatory: event.is_mandatory !== undefined ? event.is_mandatory : false,
        max_participants: event.max_participants || '',
        color_code: event.color_code || '#1565C0',
        session_mode: 'template',
        session_template_id: '',
        activities: []
      };

      setOriginalData(eventData);
      setFormData(eventData);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load event');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteCalendarEvent(eventId);
      navigate(`/admin/calendars/${formData.selected_calendar_id}/events`);
    } catch (err) {
      setError(err.message || 'Failed to delete event');
      setShowDeleteConfirm(false);
    }
  };

  if (loading || loadingCalendars || loadingRoles) {
    return (
      <AdminPageLayout
        title="EDIT EVENT"
        subtitle="Loading..."
        backPath={`/admin/calendars/${calendarId}/events`}
      >
        <LoadingState message={loading ? "Loading event..." : loadingCalendars ? "Loading calendars..." : "Loading roles..."} />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="EDIT EVENT"
      subtitle={`Editing: ${originalData.title}`}
      backPath={`/admin/calendars/${formData.selected_calendar_id || calendarId}/events`}
    >
      <div className="edit-event-page">
        {error && (
          <ErrorBanner
            type="error"
            message={error}
            onDismiss={() => setError(null)}
          />
        )}

        <form onSubmit={handleFormSubmit} className="event-form">
          <div className="form-section">
            <h3>Calendar Selection</h3>
            <p className="field-hint" style={{ marginBottom: '16px', fontSize: '0.875rem', color: '#666' }}>
              You can move this event to a different calendar if needed.
            </p>

            <FormSelect
              label="Target Calendar"
              name="selected_calendar_id"
              value={formData.selected_calendar_id}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.selected_calendar_id ? errors.selected_calendar_id : ''}
              options={calendars.map(cal => ({
                value: cal.id,
                label: cal.name
              }))}
              required
            />
          </div>

          <div className="form-section">
            <h3>Event Details</h3>

            <FormInput
              label="Event Title"
              name="title"
              value={formData.title}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.title ? errors.title : ''}
              placeholder="e.g., Weekly Training Session"
              required
            />

            <FormTextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.description ? errors.description : ''}
              placeholder="Describe the event"
              rows={3}
            />

            <FormSelect
              label="Event Type"
              name="event_type"
              value={formData.event_type}
              onChange={(e) => {
                handleFieldChange(e);
                // Reset session data when event type changes
                setSessionMode('template');
                setSelectedTemplate(null);
                setSessionActivities([]);
                setFieldValue('session_mode', 'template');
                setFieldValue('session_template_id', '');
                setFieldValue('activities', []);
              }}
              onBlur={handleFieldBlur}
              error={touched.event_type ? errors.event_type : ''}
              options={eventTypes}
              required
            />

            {TRAINING_EVENT_TYPES.includes(formData.event_type) && (
              <>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Session Creation Method
                  </label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={() => handleSessionModeChange('template')}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: sessionMode === 'template' ? '2px solid #1565C0' : '1px solid #ddd',
                        borderRadius: '4px',
                        background: sessionMode === 'template' ? '#E3F2FD' : 'white',
                        cursor: 'pointer',
                        fontWeight: sessionMode === 'template' ? '600' : '400'
                      }}
                    >
                      Use Template
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSessionModeChange('adhoc')}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: sessionMode === 'adhoc' ? '2px solid #1565C0' : '1px solid #ddd',
                        borderRadius: '4px',
                        background: sessionMode === 'adhoc' ? '#E3F2FD' : 'white',
                        cursor: 'pointer',
                        fontWeight: sessionMode === 'adhoc' ? '600' : '400'
                      }}
                    >
                      Build Custom Session
                    </button>
                  </div>
                  <p className="field-hint" style={{ marginTop: '8px', fontSize: '0.875rem', color: '#666' }}>
                    {sessionMode === 'template' 
                      ? 'Select a pre-defined session template with activities'
                      : 'Build a custom session by selecting individual activities'}
                  </p>
                </div>

                {sessionMode === 'template' && (
                  <>
                    <FormSelect
                      label="Session Template"
                      name="session_template_id"
                      value={formData.session_template_id}
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                      onBlur={handleFieldBlur}
                      error={touched.session_template_id ? errors.session_template_id : ''}
                      options={[
                        { value: '', label: '-- Select a Session Template --' },
                        ...sessionTemplates.map(t => ({
                          value: t.id,
                          label: `${t.name} (${t.total_duration_mins} mins)`
                        }))
                      ]}
                      required
                      disabled={loadingTemplates || !formData.selected_calendar_id}
                      help={loadingTemplates ? "Loading templates..." : "Select a training session template"}
                    />

                    {selectedTemplate && sessionActivities.length > 0 && (
                      <div className="form-section" style={{ marginTop: '16px' }}>
                        <h4>Session Activities</h4>
                        <p className="field-hint" style={{ marginBottom: '16px', fontSize: '0.875rem', color: '#666' }}>
                          You can adjust the duration of each activity for this event.
                        </p>
                        <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '16px' }}>
                          {sessionActivities.map((activity, index) => (
                            <div
                              key={activity.id || index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px',
                                marginBottom: index < sessionActivities.length - 1 ? '8px' : '0',
                                borderBottom: index < sessionActivities.length - 1 ? '1px solid #eee' : 'none'
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                  {activity.activity_name || `Activity ${index + 1}`}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#666' }}>
                                  {activity.activity_type && (
                                    <span style={{ marginRight: '12px' }}>
                                      Type: {activity.activity_type}
                                    </span>
                                  )}
                                  {activity.category && (
                                    <span>Category: {activity.category}</span>
                                  )}
                                </div>
                              </div>
                              <div style={{ width: '120px' }}>
                                <FormInput
                                  label="Duration (mins)"
                                  name={`activity_duration_${index}`}
                                  type="number"
                                  value={activity.planned_duration_mins}
                                  onChange={(e) => handleActivityDurationChange(index, e.target.value)}
                                  min="1"
                                  max="180"
                                  style={{ marginBottom: 0 }}
                                />
                              </div>
                            </div>
                          ))}
                          <div style={{
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: '1px solid #eee',
                            textAlign: 'right',
                            fontWeight: '500'
                          }}>
                            Total Duration: {sessionActivities.reduce((sum, a) => sum + (parseInt(a.planned_duration_mins) || 0), 0)} minutes
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {sessionMode === 'adhoc' && (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Add Activity
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <select
                          id="activity-selector"
                          style={{
                            flex: 1,
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                          disabled={loadingActivities || activityTemplates.length === 0}
                        >
                          <option value="">
                            {loadingActivities ? 'Loading activities...' : 'Select an activity to add'}
                          </option>
                          {activityTemplates.map(activity => (
                            <option key={activity.id} value={activity.id}>
                              {activity.name} ({activity.activity_type})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            const selector = document.getElementById('activity-selector');
                            if (selector.value) {
                              handleAddActivity(selector.value);
                              selector.value = '';
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#1565C0',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                          disabled={loadingActivities || activityTemplates.length === 0}
                        >
                          Add
                        </button>
                      </div>
                      {errors.activities && (
                        <p style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '4px' }}>
                          {errors.activities}
                        </p>
                      )}
                    </div>

                    {sessionActivities.length > 0 && (
                      <div className="form-section" style={{ marginTop: '16px' }}>
                        <h4>Session Activities ({sessionActivities.length})</h4>
                        <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '16px' }}>
                          {sessionActivities.map((activity, index) => (
                            <div
                              key={`${activity.activity_template_id}-${index}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px',
                                marginBottom: index < sessionActivities.length - 1 ? '8px' : '0',
                                borderBottom: index < sessionActivities.length - 1 ? '1px solid #eee' : 'none'
                              }}
                            >
                              <div style={{ marginRight: '12px', color: '#666', fontWeight: '500' }}>
                                {index + 1}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                  {activity.activity_name}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#666' }}>
                                  Type: {activity.activity_type}
                                </div>
                              </div>
                              <div style={{ width: '120px', marginRight: '12px' }}>
                                <FormInput
                                  label="Duration (mins)"
                                  name={`adhoc_activity_duration_${index}`}
                                  type="number"
                                  value={activity.planned_duration_mins}
                                  onChange={(e) => handleActivityDurationChange(index, e.target.value)}
                                  min="1"
                                  max="180"
                                  style={{ marginBottom: 0 }}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveActivity(index)}
                                style={{
                                  padding: '8px 12px',
                                  background: '#d32f2f',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem'
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <div style={{
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: '1px solid #eee',
                            textAlign: 'right',
                            fontWeight: '500'
                          }}>
                            Total Duration: {sessionActivities.reduce((sum, a) => sum + (parseInt(a.planned_duration_mins) || 0), 0)} minutes
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <FormMultiSelect
              label="Event Visibility"
              name="visibility_roles"
              value={formData.visibility_roles}
              onChange={handleFieldChange}
              options={visibilityOptions}
              error={touched.visibility_roles ? errors.visibility_roles : ''}
              required
              help="Select who can see this event. You can select multiple roles."
            />
          </div>

          <div className="form-section">
            <h3>Date & Time</h3>

            <div className="form-row">
              <FormDateTimePicker
                label="Start Time"
                name="start_time"
                value={formData.start_time}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.start_time ? errors.start_time : ''}
                required
              />

              <FormDateTimePicker
                label="End Time"
                name="end_time"
                value={formData.end_time}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.end_time ? errors.end_time : ''}
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Location & Settings</h3>

            <FormInput
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.location ? errors.location : ''}
              placeholder="Event location"
            />

            <div className="form-row">
              <FormInput
                label="Max Participants"
                name="max_participants"
                type="number"
                value={formData.max_participants}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.max_participants ? errors.max_participants : ''}
                placeholder="Leave blank for unlimited"
                min="1"
              />

              <FormColorPicker
                label="Color Code"
                name="color_code"
                value={formData.color_code}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.color_code ? errors.color_code : ''}
              />
            </div>

            <FormCheckbox
              label="Mandatory Event"
              name="is_mandatory"
              checked={formData.is_mandatory}
              onChange={(e) => setFieldValue('is_mandatory', e.target.checked)}
            />
          </div>

          <div className="form-actions">
            <div className="form-actions-left">
              <Button
                type="button"
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting}
              >
                <i className="fas fa-trash"></i>
                Delete Event
              </Button>
            </div>
            <div className="form-actions-right">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(`/admin/calendars/${calendarId}/events`)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Event"
          message={`Are you sure you want to delete the event "${formData.title}"? This action cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
        />
      </div>
    </AdminPageLayout>
  );
};

export default EditEvent;
